/**
 * Manual-book filtering — the `book` param applied to a row set (ADR-0042 · #84).
 *
 * The sibling of `filter-universe.ts`, and deliberately shaped like it: both
 * narrow a row set by a single orthogonal axis, both return a NEW array in
 * every branch including the identity one, and both are generic over the
 * minimum row shape they need. `?idx=SPX&book=1` composes the two.
 *
 * Ownership is NOT a `FilterKey`. `INDEX_CODES` has to stay byte-identical to
 * the `market.index_code` PG enum labels, so a `BOOK` member would corrupt that
 * tuple to express something that is not an index membership at all.
 *
 * `undefined` — not an empty set — is "filter off". The distinction is
 * load-bearing: an operator whose book is genuinely empty and who asks for
 * `book=1` must get ZERO rows, not the whole universe. Collapsing the two would
 * make an unwritten CSV look like a full portfolio.
 */
import type { BookLot, BookFilterParam, Ticker } from '@meridian/contracts';
import { BOOK_FILTER_ON } from '@meridian/contracts';

export interface BookedRow {
  readonly ticker: Ticker;
}

/** Resolved ownership for one request; `undefined` means the filter is off. */
export type BookOwnership = ReadonlySet<Ticker> | undefined;

/**
 * Decide ownership ONCE per request rather than at each of the three filter
 * call sites (dashboard, sector, area). Three copies of `query.book ===
 * BOOK_FILTER_ON` is three chances for one screen to disagree with the others
 * about whether the filter is engaged.
 */
export function bookOwnership(
  book: BookFilterParam | undefined,
  lots: readonly BookLot[],
): BookOwnership {
  if (book !== BOOK_FILTER_ON) return undefined;
  return new Set(lots.map((lot) => lot.ticker));
}

export function filterBook<T extends BookedRow>(
  rows: readonly T[],
  owned: BookOwnership,
): readonly T[] {
  if (owned === undefined) return [...rows];
  return rows.filter((row) => owned.has(row.ticker));
}

/** 404 identity when an index/book/compare filter empties a real sector or area. */
export function emptiedFilterId(
  id: string,
  index: string,
  book: BookFilterParam | undefined,
  compare?: readonly Ticker[],
): string {
  const withBook = book === BOOK_FILTER_ON ? ', book=1' : '';
  const withCmp = compare === undefined ? '' : `, cmp=${compare.join(',')}`;
  return `${id} (index=${index}${withBook}${withCmp})`;
}
